// ABOUTME: About page component displaying privacy policy, terms of service, and contact information
// ABOUTME: Accessible from authenticated user menu for easy policy access and support information

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Link,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Email as EmailIcon,
  Business as BusinessIcon,
  Public as WebsiteIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { getPrivacyPolicy, getTermsOfService } from '../services/api';
import type { PolicyDocument } from '../types/api';
import packageInfo from '../../package.json';

const AboutPage: React.FC = () => {
  const [policies, setPolicies] = useState<{
    privacy: PolicyDocument | null;
    terms: PolicyDocument | null;
  }>({
    privacy: null,
    terms: null
  });
  
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<{
    privacy?: string;
    terms?: string;
  }>({});

  useEffect(() => {
    const loadPolicies = async () => {
      setLoading(true);
      const newErrors: typeof errors = {};

      try {
        const [privacyResult, termsResult] = await Promise.allSettled([
          getPrivacyPolicy(),
          getTermsOfService()
        ]);

        if (privacyResult.status === 'fulfilled') {
          setPolicies(prev => ({ ...prev, privacy: privacyResult.value }));
        } else {
          newErrors.privacy = 'Failed to load Privacy Policy';
        }

        if (termsResult.status === 'fulfilled') {
          setPolicies(prev => ({ ...prev, terms: termsResult.value }));
        } else {
          newErrors.terms = 'Failed to load Terms of Service';
        }

        setErrors(newErrors);
      } catch {
        setErrors({
          privacy: 'Failed to load Privacy Policy',
          terms: 'Failed to load Terms of Service'
        });
      } finally {
        setLoading(false);
      }
    };

    loadPolicies();
  }, []);

  const renderMarkdown = (content: string) => {
    // Basic markdown rendering with sanitization
    const html = content
      .replace(/^# (.*)/gm, '<h1>$1</h1>')
      .replace(/^## (.*)/gm, '<h2>$1</h2>')
      .replace(/^### (.*)/gm, '<h3>$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>')
      .replace(/^(.*)$/, '<p>$1</p>');
    
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['h1', 'h2', 'h3', 'p', 'br', 'strong', 'em', 'ul', 'ol', 'li'],
      ALLOWED_ATTR: []
    });
  };

  const currentYear = new Date().getFullYear();

  return (
    <Container maxWidth="lg" data-testid="about-page-container" className="responsive-container">
      <Box component="main" role="main" sx={{ py: 4 }}>
        {/* Page Header */}
        <Typography variant="h3" component="h1" gutterBottom align="center">
          About
        </Typography>

        <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Information about our services, policies, and support
        </Typography>

        {/* Privacy Policy Section */}
        <Paper elevation={2} sx={{ mb: 4, p: 3 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            Privacy Policy
          </Typography>
          
          {errors.privacy ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.privacy}
            </Alert>
          ) : loading || !policies.privacy ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress role="progressbar" />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <div
                dangerouslySetInnerHTML={{
                  __html: renderMarkdown(policies.privacy.content)
                }}
              />
            </Box>
          )}
        </Paper>

        {/* Terms of Service Section */}
        <Paper elevation={2} sx={{ mb: 4, p: 3 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            Terms of Service
          </Typography>
          
          {errors.terms ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.terms}
            </Alert>
          ) : loading || !policies.terms ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress role="progressbar" />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <div
                dangerouslySetInnerHTML={{
                  __html: renderMarkdown(policies.terms.content)
                }}
              />
            </Box>
          )}
        </Paper>

        {/* Contact Information Section */}
        <Paper elevation={2} sx={{ mb: 4, p: 3 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            Contact Information
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <EmailIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Support Email"
                secondary={
                  <Link 
                    href="mailto:support@example.com"
                    underline="hover"
                    color="primary"
                  >
                    support@example.com
                  </Link>
                }
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <BusinessIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Company Name"
                secondary="Let's Reset"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <BusinessIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Address"
                secondary="Company Address"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <WebsiteIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Website"
                secondary={
                  <Link 
                    href="https://example.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    color="primary"
                  >
                    https://example.com
                  </Link>
                }
              />
            </ListItem>
          </List>
        </Paper>

        {/* App Version Section */}
        <Paper elevation={2} sx={{ mb: 4, p: 3 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            App Version
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Version"
                secondary={import.meta.env.VITE_APP_VERSION || packageInfo.version || '1.0.0'}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Application"
                secondary={packageInfo.name || 'bepono-web'}
              />
            </ListItem>
          </List>
        </Paper>

        {/* Copyright Section */}
        <Box textAlign="center" sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            © {currentYear} Let&apos;s Reset. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default AboutPage;